import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { Spinner } from '../components/Spinner';
import { supabase } from '../lib/supabase';

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  function validate(): boolean {
    const em = email.trim();
    if (!em) {
      setEmailError('Enter your email address.');
      return false;
    }
    if (!isValidEmail(em)) {
      setEmailError('Enter a valid email address.');
      return false;
    }
    setEmailError(null);
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setSubmitting(true);
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo,
    });

    if (error) {
      setFormError(error.message);
      setSubmitting(false);
      return;
    }

    setSent(true);
    setSubmitting(false);
  }

  const inputClass =
    'w-full rounded-lg border border-border bg-navy px-3 py-2.5 text-sm text-foreground outline-none transition placeholder:text-muted focus:border-accent focus:ring-1 focus:ring-accent/40';

  return (
    <main className="min-h-screen bg-navy px-4 py-10 text-foreground sm:px-6 sm:py-14">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-6 flex justify-center">
            <Logo size="lg" />
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-surface/80 p-6 shadow-xl backdrop-blur sm:p-8">
          <h1 className="text-center text-xl font-semibold sm:text-2xl">Reset Your Password</h1>

          {sent ? (
            <p className="mt-6 text-center text-sm text-muted leading-relaxed">
              Check your email. We&apos;ve sent a password reset link.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6" noValidate>
              {formError ? (
                <p
                  className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
                  role="alert"
                >
                  {formError}
                </p>
              ) : null}

              <label htmlFor="forgot-email" className="mb-1.5 block text-sm font-medium text-foreground">
                Email
              </label>
              <input
                id="forgot-email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${inputClass} ${emailError ? 'border-danger' : ''}`}
              />
              {emailError ? <p className="mt-1.5 text-sm text-danger">{emailError}</p> : null}

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-cta-text transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Spinner className="h-5 w-5 text-cta-text" />
                    Sending…
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
            </form>
          )}

          <p className="mt-8 text-center text-sm">
            <Link to="/login" className="font-medium text-accent hover:underline">
              Back to log in
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
